import React, { useEffect, useState } from 'react';
import { Card, CardBody, Col, Container, Input, Label, Row, Button } from 'reactstrap';
import Swal from "sweetalert2";
import AuthService from "../util/AuthService";

const API_URL = process.env.REACT_APP_URL_API;

const Profile = () => {
    document.title = "Perfil | Oplena";

    // Detect if user is owner or regular user
    const isOwner = (() => {
        try {
            const d = JSON.parse(localStorage.getItem("users"));
            return d?.role === "owner" || !!localStorage.getItem("owner");
        } catch { return false; }
    })();

    const [form, setForm] = useState({ name: "", email: "", phone: "", document: "" });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [avatar, setAvatar] = useState(() => localStorage.getItem("owner_avatar") || "");
    const [passwordForm, setPasswordForm] = useState({ current_password: "", new_password: "", confirm_password: "" });
    const [savingPassword, setSavingPassword] = useState(false);
    const [stripe, setStripe] = useState({ has_account: false, onboarding_complete: false });
    const [financial, setFinancial] = useState({ balance: 0, total_revenue: 0, transactions: [] });
    const [connectingStripe, setConnectingStripe] = useState(false);
    const [requestingPayout, setRequestingPayout] = useState(false);
    const [payoutAmount, setPayoutAmount] = useState("");

    const getToken = () => {
        try {
            const d = JSON.parse(localStorage.getItem("users"));
            return d?.token || "";
        } catch { return ""; }
    };

    const apiFetch = async (method, endpoint, body) => {
        const opts = {
            method,
            headers: { "Content-Type": "application/json", "Authorization": `Bearer ${getToken()}` },
        };
        if (body) opts.body = JSON.stringify(body);
        const res = await fetch(API_URL + endpoint, opts);
        return res.json();
    };

    useEffect(() => {
        (async () => {
            try {
                const meEndpoint = isOwner ? "owner/me" : "auth/me";
                const json = await apiFetch("GET", meEndpoint);
                if (json?.data) {
                    setForm({
                        name: json.data.name || "",
                        email: json.data.email || "",
                        phone: json.data.phone || "",
                        document: json.data.document || "",
                    });
                    if (json.data.avatar) {
                        setAvatar(json.data.avatar);
                        localStorage.setItem("owner_avatar", json.data.avatar);
                    }
                }
                // Fetch Stripe status + Financial (owner only)
                if (isOwner) {
                    const [stripeJson, finJson] = await Promise.all([
                        apiFetch("GET", "owner/stripe/status").catch(() => null),
                        apiFetch("GET", "owner/financial").catch(() => null),
                    ]);
                    if (stripeJson?.data) setStripe(stripeJson.data);
                    if (finJson?.data) setFinancial(finJson.data);
                }
            } catch (e) {
                console.error(e);
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const handleSubmit = async () => {
        setSaving(true);
        try {
            const profileEndpoint = isOwner ? "owner/profile" : "auth/profile";
            const json = await apiFetch("PUT", profileEndpoint, form);
            if (json?.code === 200) {
                // Update localStorage
                AuthService.updateData({ name: form.name, email: form.email, phone: form.phone });
                Swal.fire({ icon: "success", title: "Salvo!", text: "Dados atualizados com sucesso.", timer: 2000, showConfirmButton: false });
            } else {
                Swal.fire({ icon: "error", title: "Erro", text: json?.message || "Erro ao salvar." });
            }
        } catch (e) {
            Swal.fire({ icon: "error", title: "Erro", text: "Erro ao salvar dados." });
        } finally {
            setSaving(false);
        }
    };

    const handlePasswordChange = (e) => setPasswordForm({ ...passwordForm, [e.target.name]: e.target.value });

    const handlePasswordSubmit = async () => {
        if (passwordForm.new_password !== passwordForm.confirm_password) {
            Swal.fire({ icon: "warning", title: "Atenção", text: "As senhas não conferem." });
            return;
        }
        if (passwordForm.new_password.length < 6) {
            Swal.fire({ icon: "warning", title: "Atenção", text: "A nova senha deve ter no mínimo 6 caracteres." });
            return;
        }
        setSavingPassword(true);
        try {
            const pwMethod = isOwner ? "PUT" : "POST";
            const pwEndpoint = isOwner ? "owner/password" : "auth/change-password";
            const json = await apiFetch(pwMethod, pwEndpoint, {
                current_password: passwordForm.current_password,
                new_password: passwordForm.new_password,
            });
            if (json?.code === 200) {
                Swal.fire({ icon: "success", title: "Senha alterada!", timer: 2000, showConfirmButton: false });
                setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
            } else {
                Swal.fire({ icon: "error", title: "Erro", text: json?.message || "Erro ao alterar senha." });
            }
        } catch (e) {
            Swal.fire({ icon: "error", title: "Erro", text: "Erro ao alterar senha." });
        } finally {
            setSavingPassword(false);
        }
    };

    const formatDocument = (doc) => {
        if (!doc) return "";
        const d = doc.replace(/\D/g, "");
        if (d.length <= 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, "$1.$2.$3-$4");
        return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, "$1.$2.$3/$4-$5");
    };

    const handleAvatarChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            Swal.fire({ icon: "warning", title: "Imagem muito grande", text: "Máximo 5MB." });
            return;
        }
        const allowed = ["image/jpeg", "image/png", "image/webp"];
        if (!allowed.includes(file.type)) {
            Swal.fire({ icon: "warning", title: "Formato inválido", text: "Use JPG, PNG ou WebP." });
            return;
        }
        try {
            const formData = new FormData();
            formData.append("file", file);
            const res = await fetch(API_URL + "auth/avatar", {
                method: "POST",
                headers: { "Authorization": `Bearer ${getToken()}` },
                body: formData,
            });
            const json = await res.json();
            if (json?.code === 200 && json?.data?.avatar) {
                setAvatar(json.data.avatar);
                localStorage.setItem("owner_avatar", json.data.avatar);
                Swal.fire({ icon: "success", title: "Foto atualizada!", timer: 1500, showConfirmButton: false });
            } else {
                Swal.fire({ icon: "error", title: "Erro", text: json?.message || "Erro ao salvar foto." });
            }
        } catch (err) {
            Swal.fire({ icon: "error", title: "Erro", text: "Erro ao enviar foto." });
        }
    };

    const handleStripeConnect = async () => {
        setConnectingStripe(true);
        try {
            const json = await apiFetch("POST", "owner/stripe/connect");
            if (json?.data?.url) {
                window.location.href = json.data.url;
            } else {
                Swal.fire({ icon: "error", title: "Erro", text: json?.message || "Erro ao conectar Stripe." });
            }
        } catch (e) {
            Swal.fire({ icon: "error", title: "Erro", text: "Erro ao conectar com Stripe." });
        } finally {
            setConnectingStripe(false);
        }
    };

    const handlePayout = async () => {
        const amount = parseFloat(payoutAmount);
        if (!amount || amount <= 0) {
            Swal.fire({ icon: "warning", title: "Valor inválido", text: "Informe um valor válido para saque." });
            return;
        }
        if (amount > financial.balance) {
            Swal.fire({ icon: "warning", title: "Saldo insuficiente", text: "O valor solicitado excede o saldo disponível." });
            return;
        }
        const confirm = await Swal.fire({
            icon: "question", title: "Confirmar Saque",
            html: `Deseja sacar <strong>R$ ${amount.toFixed(2)}</strong>?`,
            showCancelButton: true, confirmButtonText: "Sim, sacar", cancelButtonText: "Cancelar",
        });
        if (!confirm.isConfirmed) return;
        setRequestingPayout(true);
        try {
            const json = await apiFetch("POST", "owner/stripe/payout", { amount });
            if (json?.code === 200) {
                Swal.fire({ icon: "success", title: "Saque solicitado!", text: "O valor será transferido para sua conta.", timer: 3000 });
                setPayoutAmount("");
                setFinancial(prev => ({ ...prev, balance: prev.balance - amount }));
            } else {
                Swal.fire({ icon: "error", title: "Erro", text: json?.message || "Erro ao solicitar saque." });
            }
        } catch (e) {
            Swal.fire({ icon: "error", title: "Erro", text: "Erro ao solicitar saque." });
        } finally {
            setRequestingPayout(false);
        }
    };

    const labelStyle = { fontWeight: 600, fontSize: "0.8rem", color: "#878a99", marginBottom: 4 };

    return (
        <div className="page-content">
            <Container fluid>
                <Row className="justify-content-center">
                    <Col xl={8}>
                        {/* Profile Card */}
                        <Card>
                            <CardBody className="p-4">
                                <div className="d-flex align-items-center mb-4">
                                    <div style={{ position: "relative", flexShrink: 0 }}>
                                        {avatar ? (
                                            <img src={avatar} alt="avatar" className="rounded-circle object-fit-cover"
                                                style={{ width: 72, height: 72, border: "3px solid #E5A325" }} />
                                        ) : (
                                            <div className="d-flex align-items-center justify-content-center rounded-circle"
                                                style={{ width: 72, height: 72, background: "rgba(229,163,37,0.15)", color: "#E5A325", fontWeight: 700, fontSize: 28 }}>
                                                {(form.name || "U").charAt(0).toUpperCase()}
                                            </div>
                                        )}
                                        <label htmlFor="avatar-upload" style={{
                                            position: "absolute", bottom: -2, right: -2, width: 28, height: 28,
                                            background: "#E5A325", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                                            cursor: "pointer", border: "2px solid #161921",
                                        }}>
                                            <i className="fa-solid fa-camera" style={{ fontSize: 12, color: "#0f1117" }}></i>
                                        </label>
                                        <input id="avatar-upload" type="file" accept="image/*" onChange={handleAvatarChange}
                                            style={{ display: "none" }} />
                                    </div>
                                    <div className="ms-3">
                                        <h5 className="mb-1">{form.name || "Carregando..."}</h5>
                                        <p className="text-muted mb-0">{form.email}</p>
                                    </div>
                                </div>

                                <h6 className="mb-3" style={{ color: "#E5A325", fontWeight: 600 }}>
                                    <i className="fa-solid fa-user me-2"></i>Dados Pessoais
                                </h6>

                                <Row>
                                    <Col md={6} className="mb-3">
                                        <Label style={labelStyle}>Nome</Label>
                                        <Input name="name" value={form.name} onChange={handleChange} disabled={loading} />
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <Label style={labelStyle}>E-mail</Label>
                                        <Input name="email" value={form.email} disabled />
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <Label style={labelStyle}>Telefone</Label>
                                        <Input name="phone" value={form.phone} onChange={handleChange} disabled={loading} placeholder="11999999999" />
                                    </Col>
                                    <Col md={6} className="mb-3">
                                        <Label style={labelStyle}>CPF/CNPJ</Label>
                                        <Input value={formatDocument(form.document)} disabled />
                                    </Col>
                                </Row>

                                <div className="mt-2">
                                    <button className="btn w-100 py-2 fw-semibold" onClick={handleSubmit} disabled={saving || loading}
                                        style={{ background: "rgba(229,163,37,0.15)", color: "#E5A325", borderRadius: 10, border: "1px solid rgba(229,163,37,0.3)" }}>
                                        {saving ? <><i className="fa-solid fa-spinner fa-spin me-1"></i>Salvando...</> : <><i className="fa-solid fa-check me-1"></i>Salvar</>}
                                    </button>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Password Card */}
                        <Card>
                            <CardBody className="p-4">
                                <h6 className="mb-3" style={{ color: "#E5A325", fontWeight: 600 }}>
                                    <i className="fa-solid fa-lock me-2"></i>Alterar Senha
                                </h6>

                                <Row>
                                    <Col md={4} className="mb-3">
                                        <Label style={labelStyle}>Senha Atual</Label>
                                        <Input type="password" name="current_password" value={passwordForm.current_password} onChange={handlePasswordChange} />
                                    </Col>
                                    <Col md={4} className="mb-3">
                                        <Label style={labelStyle}>Nova Senha</Label>
                                        <Input type="password" name="new_password" value={passwordForm.new_password} onChange={handlePasswordChange} />
                                    </Col>
                                    <Col md={4} className="mb-3">
                                        <Label style={labelStyle}>Confirmar Nova Senha</Label>
                                        <Input type="password" name="confirm_password" value={passwordForm.confirm_password} onChange={handlePasswordChange} />
                                    </Col>
                                </Row>

                                <div className="mt-2">
                                    <button className="btn w-100 py-2 fw-semibold" onClick={handlePasswordSubmit} disabled={savingPassword}
                                        style={{ background: "rgba(229,163,37,0.15)", color: "#E5A325", borderRadius: 10, border: "1px solid rgba(229,163,37,0.3)" }}>
                                        {savingPassword ? <><i className="fa-solid fa-spinner fa-spin me-1"></i>Alterando...</> : <><i className="fa-solid fa-key me-1"></i>Alterar Senha</>}
                                    </button>
                                </div>
                            </CardBody>
                        </Card>

                        {/* Stripe / Financial Card (owner only) */}
                        {isOwner && <Card>
                            <CardBody className="p-4">
                                <h6 className="mb-3" style={{ color: "#E5A325", fontWeight: 600 }}>
                                    <i className="fa-solid fa-credit-card me-2"></i>Stripe & Financeiro
                                </h6>

                                {/* Connection Status */}
                                <div className="d-flex align-items-center justify-content-between p-3 mb-3" style={{ background: "rgba(229,163,37,0.06)", borderRadius: 10, border: "1px solid #22252f" }}>
                                    <div className="d-flex align-items-center">
                                        <div className="d-flex align-items-center justify-content-center rounded-circle me-3"
                                            style={{ width: 42, height: 42, background: stripe.onboarding_complete ? "rgba(74,222,128,0.12)" : "rgba(239,68,68,0.12)" }}>
                                            <i className={`fa-solid ${stripe.onboarding_complete ? "fa-circle-check" : "fa-link-slash"}`}
                                                style={{ color: stripe.onboarding_complete ? "#4ade80" : "#ef4444", fontSize: 18 }}></i>
                                        </div>
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                                                {stripe.onboarding_complete ? "Stripe Conectado" : stripe.has_account ? "Configuração Pendente" : "Stripe Desconectado"}
                                            </div>
                                            <div className="text-muted" style={{ fontSize: "0.78rem" }}>
                                                {stripe.onboarding_complete
                                                    ? "Sua conta está ativa e pronta para receber pagamentos."
                                                    : "Conecte sua conta Stripe para receber pagamentos."}
                                            </div>
                                        </div>
                                    </div>
                                    {!stripe.onboarding_complete && (
                                        <Button color="primary" size="sm" onClick={handleStripeConnect} disabled={connectingStripe}>
                                            {connectingStripe
                                                ? <><i className="fa-solid fa-spinner fa-spin me-1"></i>Conectando...</>
                                                : <><i className="fa-brands fa-stripe-s me-1"></i>{stripe.has_account ? "Completar" : "Conectar"}</>}
                                        </Button>
                                    )}
                                </div>

                                {/* Balance Cards */}
                                <Row className="mb-3">
                                    <Col md={4} className="mb-3 mb-md-0">
                                        <div className="p-3 text-center" style={{ background: "#1a1d27", borderRadius: 10, border: "1px solid #22252f" }}>
                                            <div className="text-muted mb-1" style={{ fontSize: "0.75rem", fontWeight: 600 }}>SALDO DISPONÍVEL</div>
                                            <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#4ade80" }}>
                                                R$ {(financial.balance || 0).toFixed(2)}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4} className="mb-3 mb-md-0">
                                        <div className="p-3 text-center" style={{ background: "#1a1d27", borderRadius: 10, border: "1px solid #22252f" }}>
                                            <div className="text-muted mb-1" style={{ fontSize: "0.75rem", fontWeight: 600 }}>RECEITA TOTAL</div>
                                            <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#E5A325" }}>
                                                R$ {(financial.total_revenue || 0).toFixed(2)}
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="p-3 text-center" style={{ background: "#1a1d27", borderRadius: 10, border: "1px solid #22252f" }}>
                                            <div className="text-muted mb-1" style={{ fontSize: "0.75rem", fontWeight: 600 }}>COMISSÃO</div>
                                            <div style={{ fontSize: "1.5rem", fontWeight: 700, color: "#60a5fa" }}>
                                                {((financial.commission_rate || 0.1) * 100).toFixed(0)}%
                                            </div>
                                        </div>
                                    </Col>
                                </Row>

                                {/* Payout Section */}
                                <div className="p-3" style={{ background: "#1a1d27", borderRadius: 10, border: "1px solid #22252f" }}>
                                    <div className="d-flex align-items-center mb-2">
                                        <i className="fa-solid fa-money-bill-transfer me-2" style={{ color: "#E5A325" }}></i>
                                        <span style={{ fontWeight: 600, fontSize: "0.85rem" }}>Solicitar Saque</span>
                                    </div>
                                    {!stripe.onboarding_complete && (
                                        <div className="mb-2 p-2" style={{ background: "rgba(245,158,11,0.08)", borderRadius: 8, fontSize: "0.8rem", color: "#f59e0b" }}>
                                            <i className="fa-solid fa-triangle-exclamation me-1"></i>
                                            Conecte sua conta Stripe acima para habilitar saques.
                                        </div>
                                    )}
                                    <div className="d-flex gap-2">
                                        <Input
                                            type="number" placeholder="Valor (R$)" min="0" step="0.01"
                                            value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)}
                                            disabled={!stripe.onboarding_complete}
                                            style={{ maxWidth: 200 }}
                                        />
                                        <Button color="success" onClick={handlePayout} disabled={requestingPayout || !payoutAmount || !stripe.onboarding_complete}>
                                            {requestingPayout
                                                ? <><i className="fa-solid fa-spinner fa-spin me-1"></i>Processando...</>
                                                : <><i className="fa-solid fa-arrow-right-from-bracket me-1"></i>Sacar</>}
                                        </Button>
                                    </div>
                                    {financial.balance <= 0 && stripe.onboarding_complete && (
                                        <small className="text-muted mt-2 d-block">Nenhum saldo disponível para saque no momento.</small>
                                    )}
                                </div>

                                {/* Transactions History */}
                                {financial.transactions?.length > 0 && (
                                    <div className="mt-3">
                                        <h6 className="mb-2" style={{ fontSize: "0.85rem", fontWeight: 600 }}>Últimas Transações</h6>
                                        <div style={{ maxHeight: 250, overflowY: "auto" }}>
                                            {financial.transactions.map((tx, i) => (
                                                <div key={i} className="d-flex align-items-center justify-content-between py-2 px-2"
                                                    style={{ borderBottom: "1px solid #22252f", fontSize: "0.82rem" }}>
                                                    <div className="d-flex align-items-center">
                                                        <i className={`fa-solid ${tx.type === "payout" ? "fa-arrow-up" : "fa-arrow-down"} me-2`}
                                                            style={{ color: tx.type === "payout" ? "#ef4444" : "#4ade80" }}></i>
                                                        <span>{tx.description || tx.type}</span>
                                                    </div>
                                                    <div>
                                                        <span style={{ fontWeight: 600, color: tx.type === "payout" ? "#ef4444" : "#4ade80" }}>
                                                            {tx.type === "payout" ? "-" : "+"}R$ {(tx.amount || 0).toFixed(2)}
                                                        </span>
                                                        <span className="text-muted ms-2" style={{ fontSize: "0.72rem" }}>
                                                            {tx.created_at ? new Date(tx.created_at).toLocaleDateString("pt-BR") : ""}
                                                        </span>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </CardBody>
                        </Card>}
                    </Col>
                </Row>
            </Container>
        </div>
    );
};

export default Profile;
